import React from "react";

import { cn } from "./cn";
import { Button } from "./Button";
import { Input } from "./Input";
import { CountrySelect } from "./CountrySelect";
import { FieldLabel } from "./FieldLabel";
import { SelectField } from "./SelectField";
import { ErrorNote } from "./ErrorNote";
import { PhoneCountrySelect } from "./PhoneCountrySelect";
import PhoneNumberInput from "../PhoneNumberInput";

/* Add or edit a billing address, in u1. */
export const BillingAddressForm = ({ form, onCancel, className = "" }) => {
  const { values, errors, apiError, states, isEdit } = form;

  return (
    <div
      className={cn(
        "w-[min(94vw,720px)] rounded-[var(--radius-modal)] border border-[var(--line)]",
        "bg-white p-5 sm:p-6",
        className
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-[18px] font-bold text-[var(--ink)] font-sans">
          {isEdit ? "Edit billing address" : "Add billing address"}
        </h2>
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Back
          </Button>
        )}
      </div>

      <form onSubmit={form.submit} noValidate>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Business Name"
            id="u1-billing-company"
            value={values.companyName}
            onChange={(e) => form.setField("companyName", e.target.value)}
            error={errors.companyName}
            placeholder="Enter business name"
          />
          <Input
            label="Tax Registration Number"
            id="u1-billing-trn"
            value={values.trnNo}
            onChange={(e) => form.setField("trnNo", e.target.value)}
            error={errors.trnNo}
            placeholder="Enter tax registration number"
          />

          <Input
            label="Address Line 1"
            id="u1-billing-line1"
            value={values.address_line_1}
            onChange={(e) => form.setField("address_line_1", e.target.value)}
            error={errors.address_line_1}
            placeholder="Suite/Building/Floor No."
          />
          <Input
            label="Address Line 2"
            id="u1-billing-line2"
            value={values.address_line_2}
            onChange={(e) => form.setField("address_line_2", e.target.value)}
            error={errors.address_line_2}
            placeholder="Street, Area, etc."
          />

          <CountrySelect
            label="Country"
            id="u1-billing-country"
            value={values.country}
            onChange={(c) => form.setCountry(c?.code)}
            placeholder="Select a country"
            error={errors.country}
          />

          {/* Transcribed: a country with a state list offers it; one without
              leaves the field free text, which validateState allows. */}
          {states.length > 0 ? (
            <SelectField
              label="State"
              id="u1-billing-state"
              value={values.state}
              onChange={(e) => form.setField("state", e.target.value)}
              placeholder="Select a state"
              options={states.map((st) => ({
                value: st.name,
                label: st.name,
              }))}
              error={errors.state}
            />
          ) : (
            <Input
              label="State"
              id="u1-billing-state"
              value={values.state}
              onChange={(e) => form.setField("state", e.target.value)}
              error={errors.state}
              placeholder="Enter a state"
            />
          )}

          <Input
            label="City"
            id="u1-billing-city"
            value={values.city}
            onChange={(e) => form.setField("city", e.target.value)}
            error={errors.city}
            placeholder="Enter city"
          />
          <Input
            label="Landmark"
            id="u1-billing-area"
            value={values.area}
            onChange={(e) => form.setField("area", e.target.value)}
            error={errors.area}
            placeholder="Enter landmark"
          />

          <Input
            label="Postal Code"
            id="u1-billing-postal"
            value={values.postal_code}
            onChange={(e) => form.setField("postal_code", e.target.value)}
            error={errors.postal_code}
            placeholder="Enter postal code"
          />

          <div>
            {/* PhoneNumberInput renders its own control, so this is a span
                rather than a label pointing at nothing. */}
            <FieldLabel as="span">Mobile Number</FieldLabel>
            <PhoneNumberInput
              id="u1-billing-mobile"
              phone={values.mobile}
              setPhone={(next) => form.setField("mobile", next || "")}
              border
              verifyPhoneNumber={false}
              countrySelect={PhoneCountrySelect}
              defaultCountry="AE"
            />
            {errors.mobile && (
              <p role="alert" className="mt-1.5 text-[12px] text-[var(--coral-deep)] font-sans">
                {errors.mobile}
              </p>
            )}
          </div>
        </div>

        {apiError && <ErrorNote className="mt-4">{apiError}</ErrorNote>}

        <Button
          type="submit"
          variant="coralSolid"
          className="mt-5 w-full"
          disabled={form.saving}
        >
          {form.saving ? "Saving…" : "Save changes"}
        </Button>
      </form>
    </div>
  );
};

export default BillingAddressForm;
